import { SearchURLGenerator } from './Result';
import { defaultShortCountryCode } from '../components/Common/constants';

export const COUNTRY_CODE_TO_PATH = {
  LK: 'Sri-Lanka',
  SG: 'Singapore',
  MY: 'Malaysia',
  AUS: 'Australia',
  AU: 'Australia',
  IN: 'India',
  GB: 'United-Kingdom',
  US: 'United-States',
};

export const EVENT_TYPE_TO_CATEGORY = {
  WORK_HOTD: 'Work',
  WORK_PRIV: 'Work',
  WORK_VIRT: 'Work',
  MEET_MEET: 'Meet',
  MEET_INTE: 'Meet',
  MEET_OFFS: 'Meet',
  TRAI_WORK: 'Meet',
  SHOO_PHOT: 'Shoot',
  SHOO_VIDE: 'Shoot',
};

export const SPACE_TYPE_NAME_TO_CODE = {
  'individual workspace': 'WORK_HOTD',
  'private office': 'WORK_PRIV',
  'virtual office': 'WORK_VIRT',
  'meeting space': 'MEET_MEET',
  'meeting room': 'MEET_MEET',
  'interview room': 'MEET_INTE',
  'team building': 'MEET_OFFS',
  training: 'TRAI_WORK',
  'photo shoot': 'SHOO_PHOT',
  'video shoot': 'SHOO_VIDE',
};

/** SEO-friendly city + space-type landing paths, e.g. /Sri-Lanka/Colombo/meeting-rooms */
export const GEO_SEARCH_LANDING_SLUGS = {
  'meeting-rooms': { eventType: 'MEET_MEET', category: 'Meet', label: 'Meeting Rooms' },
  'individual-workspaces': { eventType: 'WORK_HOTD', category: 'Work', label: 'Individual Workspaces' },
  'coworking-spaces': { eventType: 'WORK_HOTD', category: 'Work', label: 'Coworking Spaces' },
  'private-offices': { eventType: 'WORK_PRIV', category: 'Work', label: 'Private Offices' },
  'virtual-offices': { eventType: 'WORK_VIRT', category: 'Work', label: 'Virtual Offices' },
  'training-rooms': { eventType: 'TRAI_WORK', category: 'Meet', label: 'Training Rooms' },
  'photo-shoot-studios': { eventType: 'SHOO_PHOT', category: 'Shoot', label: 'Photo Shoot Studios' },
};

export const GEO_SEARCH_LANDING_SLUG_PATTERN = Object.keys(GEO_SEARCH_LANDING_SLUGS).join('|');

const CITY_COORDINATES = {
  colombo: { lat: 6.927079, lng: 79.861244 },
  kandy: { lat: 7.290572, lng: 80.633728 },
  galle: { lat: 6.053519, lng: 80.220978 },
  negombo: { lat: 7.200798, lng: 79.873684 },
  jaela: { lat: 6.989627, lng: 79.888258 },
  matara: { lat: 5.954922, lng: 80.554956 },
};

const toPathSegment = (value) => (
  value ? String(value).trim().replace(/\s+/g, '-') : null
);

const fromPathSegment = (value) => (
  value ? String(value).replace(/-/g, ' ') : null
);

export const resolveEventTypeCode = (filters = {}, searchContext = {}) => {
  const ctxType = searchContext?.spaceType || searchContext?.eventType;
  if (ctxType && /^[A-Z]+_[A-Z]+$/.test(ctxType)) return ctxType;

  const displayName = filters?.spaceType?.trim().toLowerCase();
  if (displayName && SPACE_TYPE_NAME_TO_CODE[displayName]) {
    return SPACE_TYPE_NAME_TO_CODE[displayName];
  }

  return null;
};

const resolveCityCoordinates = (locationName) => {
  if (!locationName) return null;
  const key = String(locationName).trim().toLowerCase();
  return CITY_COORDINATES[key] || null;
};

/**
 * Build a country-scoped /spaces results URL with optional city path segment.
 */
export const buildSearchResultsUrl = ({
  countryCode = defaultShortCountryCode,
  countryLongCode,
  city,
  filters = {},
  searchContext = null,
  openMap = false,
  eventType: eventTypeOverride,
  category: categoryOverride,
} = {}) => {
  const ctx = searchContext || {};
  const country = countryCode || ctx.country || defaultShortCountryCode;
  const countryPath = countryLongCode || COUNTRY_CODE_TO_PATH[country] || 'Sri-Lanka';
  const eventType = eventTypeOverride || resolveEventTypeCode(filters, ctx);
  const locationOrSpace = filters?.location || ctx?.location || null;
  const cityLabel = city || locationOrSpace;
  const coords = (ctx.lat && ctx.lng)
    ? { lat: ctx.lat, lng: ctx.lng }
    : resolveCityCoordinates(locationOrSpace || city);

  const queryFilters = { country };
  if (eventType) queryFilters.eventType = eventType;
  if (categoryOverride || ctx.category) {
    queryFilters.category = categoryOverride || ctx.category;
  } else if (eventType && EVENT_TYPE_TO_CATEGORY[eventType]) {
    queryFilters.category = EVENT_TYPE_TO_CATEGORY[eventType];
  }
  if (locationOrSpace) queryFilters.locationOrSpace = locationOrSpace;
  if (coords?.lat) queryFilters.lat = coords.lat;
  if (coords?.lng) queryFilters.lng = coords.lng;
  if (filters?.date || ctx?.date) {
    queryFilters.startDate = filters?.date || ctx.date;
    if (ctx?.endDate) queryFilters.endDate = ctx.endDate;
  }
  if (openMap) queryFilters.map = 'true';

  const query = SearchURLGenerator(queryFilters);
  const citySegment = toPathSegment(cityLabel);
  if (citySegment) {
    return `/${countryPath}/${citySegment}/spaces?${query}`;
  }
  return `/${countryPath}/spaces?${query}`;
};

export const buildGeoLandingUrl = (countryLongCode, city, slug) => {
  const mapping = GEO_SEARCH_LANDING_SLUGS[slug];
  if (!mapping) return null;
  const cityLabel = fromPathSegment(city) || city;
  return buildSearchResultsUrl({
    countryLongCode,
    city: cityLabel,
    filters: { location: cityLabel },
    eventType: mapping.eventType,
    category: mapping.category,
  });
};

export const buildAgentSearchResultsUrl = (filters, searchContext, options = {}) => (
  buildSearchResultsUrl({
    countryCode: searchContext?.country || filters?.country || defaultShortCountryCode,
    filters,
    searchContext,
    openMap: options.openMap,
  })
);
